/**
 * プロンプト管理画面の純粋ロジック
 *
 * @graph-stack creative
 * @graph-domain Marketing
 * @graph-business プロンプトテンプレートのプレースホルダ抽出・未宣言変数検出・プレビュー変数生成の純粋ロジック。
 * @graph-connects none
 */
import type { PromptVariable } from '@cortex/domain-creative/web';

/**
 * テンプレートから `{{NAME}}` プレースホルダを抽出する (出現順・重複排除)。
 * @param template プロンプトテンプレート
 * @returns プレースホルダ名のリスト
 * @graph-connects none
 */
export function extractPlaceholders(template: string): string[] {
  const names = new Set<string>();
  template.replace(/\{\{([A-Z0-9_]+)\}\}/g, (placeholder: string, name: string) => {
    names.add(name);
    return placeholder;
  });
  return [...names];
}

/**
 * テンプレート内で使用しているが、タイプ定義に宣言されていない変数名を返す。
 * @param template system + user を連結したテンプレート
 * @param declared タイプ定義の変数一覧
 * @returns 未宣言のプレースホルダ名
 * @graph-connects none
 */
export function findUndeclaredPlaceholders(
  template: string,
  declared: readonly PromptVariable[],
): string[] {
  const declaredNames = new Set(declared.map((variable) => variable.name));
  return extractPlaceholders(template).filter((name) => !declaredNames.has(name));
}

/**
 * 変数展開プレビュー用の値マップ (`{{X}}` → `＜Xの説明＞`) を生成する。
 * @param variables タイプ定義の変数一覧
 * @returns プレビュー用の変数値マップ
 * @graph-connects none
 */
export function buildPreviewVariables(
  variables: readonly PromptVariable[],
): Record<string, string> {
  return Object.fromEntries(
    variables.map((variable) => [variable.name, `＜${variable.description}＞`]),
  );
}
